import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ListFilterBar } from '@/components/ListFilterBar';
import { useListFilters } from '@/lib/useListFilters';
import { useFilterOptions } from '@/lib/useFilterOptions';
import { IN_WORK_GROUP, isInWork } from '@/lib/shipmentGroups';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { DataTable, type Column } from '@/components/DataTable';
import { StatusBadge, statusLabel } from '@/components/StatusBadge';
import { RequirePermission } from '@/components/RequirePermission';
import { ErrorBanner } from '@/components/ErrorBanner';
import { ApiError } from '@/api/client';
import { useAuth } from '@/auth/AuthContext';
import { newIdempotencyKey } from '@/pages/Orders/Create';
import { RevisionMark } from '@/components/RequestRevisionNotice';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatDate, formatNumber } from '@/i18n/format';
import type { FulfilmentModel, OrderRow } from '@/api/types';
import { ShipmentDocumentsListPage } from './DocumentsList';

const LIMIT = 50;
const STATUSES = ['NEW', 'RESERVED', 'PICKING', 'PICKED', 'PACKING', 'PACKED', 'READY', 'SHIPPED', 'CANCELLED'];

/**
 * The working "Shipments" section: one list of the company's outbound orders with their physical shipment and tracking
 * (the former "Orders" and "Shipments" screens merged). Tabs by fulfilment type filter ON THE SERVER by the recorded
 * sales_order.fulfilment_model (never by channel: an FBS order can be created by hand): All | Standard | FBS | Not classified
 * (old orders nobody has classified - they are shown, never hidden or guessed). The tab lives in the URL (`type`), so paging,
 * filters and sort keep working per tab. The old physical-shipment list is the `?view=documents` view of the same section.
 */
export function ShipmentsListPage(): JSX.Element {
  const [params] = useSearchParams();
  if (params.get('view') === 'documents') return <ShipmentDocumentsListPage />;
  return <UnifiedList />;
}

const TABS: readonly { readonly value: '' | FulfilmentModel; readonly label: 'shipments.tabAll' | 'shipments.tabStandard' | 'shipments.tabFbs' | 'shipments.tabUnknown' }[] = [
  { value: '', label: 'shipments.tabAll' },
  { value: 'STANDARD', label: 'shipments.tabStandard' },
  { value: 'FBS', label: 'shipments.tabFbs' },
  { value: 'UNKNOWN', label: 'shipments.tabUnknown' },
];

function UnifiedList(): JSX.Element {
  const [offset, setOffset] = useState(0);
  const [params, setParams] = useSearchParams();
  const rawType = params.get('type');
  const type: '' | FulfilmentModel = rawType === 'STANDARD' || rawType === 'FBS' || rawType === 'UNKNOWN' ? rawType : '';
  const list = useListFilters(() => setOffset(0));
  const result = useApi(
    () => endpoints.orders({ ...list.query, ...(type ? { fulfilmentModel: type } : {}), limit: LIMIT, offset }),
    [list.key, type, offset],
  );

  // --- bulk classification of SELECTED unclassified orders (ORDERS.WRITE, "Not classified" tab only) ------------------
  const { hasPermission } = useAuth();
  const canBulk = type === 'UNKNOWN' && hasPermission('ORDERS.WRITE');
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [pending, setPending] = useState<'STANDARD' | 'FBS' | null>(null);
  const [busy, setBusy] = useState(false);
  const [bulkError, setBulkError] = useState<unknown>(null);
  const [bulkNotice, setBulkNotice] = useState<string | null>(null);
  const opKey = useRef(newIdempotencyKey());
  // a different page / tab / filter is a different selection; so is any change of the chosen orders (a new key per submission)
  useEffect(() => {
    setSelected(new Set());
    setPending(null);
    setBulkError(null);
  }, [list.key, type, offset]);
  function toggle(id: string, on: boolean): void {
    const next = new Set(selected);
    if (on) next.add(id);
    else next.delete(id);
    setSelected(next);
    setPending(null);
    setBulkError(null);
    opKey.current = newIdempotencyKey();
  }
  async function submitBulk(): Promise<void> {
    if (!pending || selected.size === 0) return;
    setBusy(true);
    setBulkError(null);
    try {
      const res = await endpoints.bulkDecideFulfilmentModel({ opId: opKey.current, orderIds: [...selected], fulfilmentModel: pending });
      setBulkNotice(t('shipments.bulk.done', { updated: res.updated, unchanged: res.unchanged }));
      setSelected(new Set());
      setPending(null);
      opKey.current = newIdempotencyKey();
      result.reload();
    } catch (err) {
      setBulkError(err);
    } finally {
      setBusy(false);
    }
  }

  function chooseTab(next: '' | FulfilmentModel): void {
    const out = new URLSearchParams(params);
    if (next) out.set('type', next);
    else out.delete('type');
    setParams(out, { replace: true });
    setOffset(0);
  }
  const options = useFilterOptions();
  const { t, locale } = useLanguage();

  const columns: readonly Column<OrderRow>[] = [
    ...(canBulk
      ? [{
          key: 'select',
          header: t('shipments.bulk.select'),
          render: (r: OrderRow) => (
            <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
              <input type="checkbox" checked={selected.has(r.id)} aria-label={t('shipments.bulk.selectAria', { number: r.number })} onChange={(e) => toggle(r.id, e.target.checked)} />
            </span>
          ),
        }]
      : []),
    { key: 'number', header: t('shipments.colOrder'), render: (r) => <>{r.number}<RevisionMark revisionNo={r.revision_no} /></> },
    { key: 'owner', header: t('inbounds.colSeller'), render: (r) => r.owner_name },
    { key: 'warehouse', header: t('inbounds.colWarehouse'), render: (r) => r.warehouse_name ?? '—' },
    { key: 'channel', header: t('queue.colChannel'), render: (r) => r.channel_name ?? (r.requested_in_portal ? t('shipments.sourceSeller') : '—') },
    { key: 'created', header: t('filters.created'), render: (r) => (r.created_at ? formatDate(r.created_at, locale) : '—') },
    {
      key: 'state',
      header: t('common.status'),
      render: (r) => (
        <>
          <StatusBadge value={r.state} />
          {isInWork(r.state) && <span className="badge" style={{ marginLeft: 6 }}>{t('shipments.inWork')}</span>}
        </>
      ),
    },
    { key: 'model', header: t('shipments.colModel'), render: (r) => <span className={`badge${r.fulfilment_model === 'UNKNOWN' ? ' warn' : ''}`}>{t(`fulfilment.${r.fulfilment_model ?? 'UNKNOWN'}`)}</span> },
    { key: 'shipment', header: t('shipments.colShipment'), render: (r) => (r.shipment_number ? <>{r.shipment_number} {r.shipment_state && <StatusBadge value={r.shipment_state} />}</> : '—') },
    { key: 'tracking', header: t('shipments.colTracking'), render: (r) => [r.carrier_kind, r.carrier_ref].filter(Boolean).join(' · ') || '—' },
    { key: 'reserved', header: t('orders.colReserved'), render: (r) => `${formatNumber(r.qty_reserved, locale)}/${formatNumber(r.qty_ordered, locale)}`, align: 'right' },
    { key: 'picked', header: t('orders.colPicked'), render: (r) => `${formatNumber(r.qty_picked, locale)}/${formatNumber(r.qty_ordered, locale)}`, align: 'right' },
    { key: 'packed', header: t('orders.colPacked'), render: (r) => `${formatNumber(r.qty_packed, locale)}/${formatNumber(r.qty_ordered, locale)}`, align: 'right' },
    { key: 'due', header: t('orders.colDue'), render: (r) => (r.due_at ? formatDate(r.due_at, locale) : '—') },
  ];

  const statuses = [
    { value: IN_WORK_GROUP, label: t('shipments.groupInWork') },
    ...STATUSES.map((s) => ({ value: s, label: statusLabel(t, s) })),
  ];

  return (
    <div>
      <div className="page-header">
        <h1>{t('nav.shipments')}</h1>
        <RequirePermission permission="ORDERS.WRITE">
          <Link className="button-link" to="/orders/new">
            {t('orders.newOrder')}
          </Link>
        </RequirePermission>
      </div>
      <div className="tabs" role="tablist" aria-label={t('shipments.tabsAria')}>
        {TABS.map((tab) => (
          <button key={tab.value || 'all'} type="button" role="tab" aria-selected={type === tab.value} className={`tab${type === tab.value ? ' active' : ''}`} onClick={() => chooseTab(tab.value)}>
            {t(tab.label)}
          </button>
        ))}
        <Link className="tab-link" to="/shipments?view=documents">{t('shipments.viewDocuments')}</Link>
      </div>
      <ListFilterBar
        filters={list.filters}
        onChange={list.set}
        onReset={list.reset}
        filtered={list.filtered}
        statuses={statuses}
        owners={options.sellers}
        warehouses={options.warehouses}
      />
      {canBulk && (
        <div className="card" aria-live="polite">
          <p className="field-hint" style={{ marginTop: 0 }}>{t('shipments.bulk.hint')}</p>
          {bulkNotice && <div className="success-card card" role="status">{bulkNotice}</div>}
          {result.status === 'success' && result.data.rows.length > 0 && (
            <label style={{ display: 'inline-flex', gap: 6, marginRight: 12 }}>
              <input
                type="checkbox"
                checked={result.data.rows.every((r) => selected.has(r.id))}
                onChange={(e) => { setSelected(e.target.checked ? new Set(result.data.rows.map((r) => r.id)) : new Set()); setPending(null); setBulkError(null); setBulkNotice(null); opKey.current = newIdempotencyKey(); }}
              />
              {t('shipments.bulk.selectAll')}
            </label>
          )}
          <strong>{t('shipments.bulk.count', { n: selected.size })}</strong>
          {!pending && (
            <div className="form-actions">
              <button type="button" disabled={selected.size === 0} onClick={() => { setPending('STANDARD'); setBulkNotice(null); }}>{t('shipments.bulk.setStandard')}</button>
              <button type="button" disabled={selected.size === 0} onClick={() => { setPending('FBS'); setBulkNotice(null); }}>{t('shipments.bulk.setFbs')}</button>
            </div>
          )}
          {pending && (
            <div role="group" aria-label={t('shipments.card.fulfilment')}>
              <p>{t('shipments.bulk.confirm', { model: t(`fulfilment.${pending}`), n: selected.size })}</p>
              {bulkError instanceof ApiError && bulkError.code === 'ORDER.FULFILMENT_BULK_CONFLICT' ? (
                <div className="error-banner" role="alert">{t('shipments.bulk.conflict')}</div>
              ) : bulkError instanceof ApiError && bulkError.code === 'COMMON.NOT_FOUND' ? (
                <div className="error-banner" role="alert">{t('shipments.bulk.notFound')}</div>
              ) : (
                Boolean(bulkError) && <ErrorBanner error={bulkError} />
              )}
              <div className="form-actions">
                <button type="button" className="primary" disabled={busy} onClick={() => void submitBulk()}>{t('common.confirm')}</button>
                <button type="button" disabled={busy} onClick={() => { setPending(null); setBulkError(null); }}>{t('common.cancel')}</button>
              </div>
            </div>
          )}
        </div>
      )}
      <DataTable
        result={result}
        columns={columns}
        getRowHref={(r) => `/shipments/${r.id}`}
        limit={LIMIT}
        offset={offset}
        onPageChange={setOffset}
        emptyTitle={list.filtered || type ? t('filters.noMatch') : t('orders.emptyTitle')}
      />
    </div>
  );
}
